import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Flame, BellRing, Navigation, CheckCircle2, Utensils, ShoppingBag, Bike,
  Download, Plus, ChevronLeft, ChevronRight,
  ArrowUpDown, SlidersHorizontal, List, LayoutGrid, Columns3,
  CheckCircle, Clock, Search, Bell,
} from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react/macro';
import { TicketTotal } from '@/components/orders/TicketTotal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/layout/PageHeaderSlot';
import { LocationSelector } from '@/components/layout/LocationSelector';
import {
  Table, TableBody, TableHeader,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useIsTablet } from '@/hooks/use-mobile';
import { useActiveLocation } from '@/contexts/ActiveLocationContext';
import { useOrders } from '@/hooks/orders/queries';
import { orderKeys } from '@/hooks/orders/keys';
import { useBulkActionCookingSessions } from '@/hooks/cooking-sessions/mutations';
import { usePassOrder, useCompleteOrder } from '@/hooks/orders/mutations';
import type { BoardOrder, BoardSessionStatus, ServiceType } from '@/lib/api/services/orders.api';
import { SectionHeader, initials } from "@/components/orders/board/OrderPrimitives";
import { KpiStat } from "@/components/common/KpiStat";
import {
  CookingCard, PlatingCard, PickupCard, DeliveryCard,
} from "@/components/orders/board/OrderCards";
import {
  CookingRow, CookingRowHeader,
  PlatingRow, PlatingRowHeader,
  PickupRow, PickupRowHeader,
  DeliveryRow, DeliveryRowHeader,
  CompletedRow, CompletedRowHeader,
} from "@/components/orders/board/OrderRows";
import { OrdersKanban } from "@/components/orders/board/OrderKanban";
import { FeatureGate } from "@/components/auth/FeatureGate";

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/(protected)/_layout/orders')({
  component: OrdersRoute,
});

// Tickets are opt-in per organization (T-1106), and the URL is reachable
// whether or not the nav entry is.
function OrdersRoute() {
  return (
    <FeatureGate feature="kdsOrders" from="/orders">
      <OrdersPage />
    </FeatureGate>
  );
}

// ─── Board stage derivation ───────────────────────────────────────────────────

type BoardStage = 'cooking' | 'plating' | 'pickup' | 'delivery' | 'completed';

function deriveStage(order: BoardOrder): BoardStage {
  if (order.isActive) return 'cooking';
  if (order.isPlating) return 'plating';
  if (order.status === 'COMPLETED') return 'completed';
  if (order.serviceType === 'TAKEOUT') return 'pickup';
  if (order.serviceType === 'DELIVERY') return 'delivery';
  return 'completed';
}

// ─── KPI bar ─────────────────────────────────────────────────────────────────

type KpiKey = 'cooking' | 'plating' | 'pickup' | 'delivery' | 'completed';

interface KpiCardProps {
  id: KpiKey;
  label: string;
  count: number;
  icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  iconColor: string;
  onClick: (id: KpiKey) => void;
}

function KpiCard({ id, label, count, icon: Icon, iconBg, iconColor, onClick }: KpiCardProps) {
  return (
    <KpiStat
      size="large"
      icon={Icon}
      iconClassName={cn(iconBg, iconColor)}
      count={count}
      label={label}
      onClick={() => onClick(id)}
      data-testid={`kpi-${id}`}
    />
  );
}

// ─── useNow hook ─────────────────────────────────────────────────────────────
// Returns current epoch ms; refreshes every 30 s via state (not direct Date.now()
// calls during render, which the React Compiler prohibits as impure).
function useNow(intervalMs = 30_000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

// ─── "Just out the door" recent rail ─────────────────────────────────────────

function RecentOrderCard({ order, now }: { order: BoardOrder; now: number }) {
  const { t } = useLingui();
  const ts = order.passedAt ?? order.sentAt;
  const mins = ts ? Math.round((now - new Date(ts).getTime()) / 60_000) : null;
  const timeLabel = mins === null ? '—' : mins < 1 ? t`just now` : t`${mins}m ago`;

  const SvcIcon = order.serviceType === 'DINE_IN' ? Utensils
    : order.serviceType === 'TAKEOUT' ? ShoppingBag : Bike;
  const svcLabel = order.serviceType === 'DINE_IN' ? t`Dine-in`
    : order.serviceType === 'TAKEOUT' ? t`Takeout` : t`Delivery`;

  return (
    <article className="relative shrink-0 w-70 h-42.5 rounded-[0.875rem] overflow-hidden cursor-pointer transition-[transform,box-shadow] duration-200 hover:-translate-y-0.75 hover:shadow-[0_18px_38px_-18px_rgba(0,0,0,.35)]">
      {order.imageUrl ? (
        <img src={order.imageUrl} alt={order.title} className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-amber-800 to-amber-950" />
      )}
      <div className="absolute inset-0 bg-(image:--color-scrim-photo-bottom)" />
      <div className="relative z-10 h-full flex flex-col justify-between px-3.5 py-3 text-white">
        {/* Top: ticket # hero (left) · time + service pills (right, stacked) */}
        <div className="flex items-start justify-between gap-2">
          <div className="inline-flex items-baseline gap-0.5 leading-none" style={{ textShadow: '0 2px 14px rgba(0,0,0,.45)' }}>
            <span className="text-[1.25rem] font-bold text-yellow-400/70">#</span>
            <span className="text-[2.125rem] font-extrabold tracking-tight text-yellow-400">{String(order.id).padStart(3, '0')}</span>
          </div>
          <div className="flex flex-col items-end gap-1.5 pt-0.5">
            <span className="inline-flex items-center gap-1 px-[0.55rem] py-1 rounded-full text-[0.6875rem] font-bold bg-white/95 text-(--brand-black) backdrop-blur-sm whitespace-nowrap">
              <Clock className="w-3 h-3 shrink-0" />
              {timeLabel}
            </span>
            <span className="inline-flex items-center gap-1 px-[0.55rem] py-1 rounded-full text-[0.6875rem] font-bold bg-white/18 text-white border border-white/25 backdrop-blur-sm whitespace-nowrap">
              <SvcIcon className="w-3 h-3 shrink-0" />
              {svcLabel}
            </span>
          </div>
        </div>
        {/* Bottom: title · chef / total */}
        <div>
          <div className="text-[0.9375rem] font-bold leading-[1.2] text-white mb-2 text-balance">{order.title}</div>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0 text-[0.78125rem]">
              {order.chefName && (
                <>
                  <div className="w-5.5 h-5.5 rounded-full bg-white/20 border border-white/70 flex items-center justify-center text-[0.5625rem] font-bold shrink-0">
                    {initials(order.chefName)}
                  </div>
                  <span className="text-white/80 truncate">{order.chefName}</span>
                </>
              )}
            </div>
            <span className="text-[0.875rem] font-extrabold text-white shrink-0">
              <TicketTotal total={order.totalPrice} />
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}

function RecentRail({ orders, className }: { orders: BoardOrder[]; className?: string }) {
  const now = useNow();
  const railRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: 'left' | 'right') => {
    railRef.current?.scrollBy({ left: dir === 'right' ? 300 : -300, behavior: 'smooth' });
  };

  if (orders.length === 0) return null;

  return (
    <section className={className}>
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-[1.25rem] font-extrabold tracking-tight"><Trans>Just out the door</Trans></h2>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-(--color-order-completed-bg) text-(--color-order-completed-fg) text-[0.6875rem] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-(--color-success) animate-pulse" />
              <Trans>Last 30 min</Trans>
            </span>
          </div>
          <p className="text-[0.8125rem] text-(--color-text-secondary) mt-0.5">
            <Trans>Wins from the line — swipe to scroll.</Trans>
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-lg" onClick={() => scroll('left')}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-lg" onClick={() => scroll('right')}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <div
        ref={railRef}
        className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory"
        style={{ scrollbarWidth: 'thin' }}
      >
        {orders.map(o => <RecentOrderCard key={o.id} order={o} now={now} />)}
      </div>
    </section>
  );
}

// ─── Skeleton card ────────────────────────────────────────────────────────────

function CardSkeleton() {
  return (
    <div className="bg-(--color-surface-muted) rounded-2xl px-3 pt-3 pb-4 flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <Skeleton className="w-13 h-13 rounded-full shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-2/3" />
      <Skeleton className="h-11 w-full rounded-xl" />
    </div>
  );
}

function RowSkeleton() {
  return (
    <div className="bg-(--color-surface-muted) rounded-xl px-3 py-3 flex items-center gap-3">
      <Skeleton className="w-9.5 h-9.5 rounded-[0.55rem] shrink-0" />
      <Skeleton className="h-4 w-16" />
      <Skeleton className="h-4 flex-1" />
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-4 w-20" />
      <Skeleton className="h-7 w-28" />
      <Skeleton className="h-11 w-28 rounded-xl" />
    </div>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────

function Toast({ message, visible }: { message: string; visible: boolean }) {
  return (
    <div className={cn(
      'fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-(--color-text-primary) text-(--color-surface-page) px-5 py-3 rounded-xl shadow-2xl',
      'transition-all duration-300',
      visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none',
    )}>
      <CheckCircle className="w-5 h-5 shrink-0 text-(--color-success-text)" />
      <span className="text-[0.875rem] font-semibold whitespace-nowrap">{message}</span>
    </div>
  );
}

// ─── Page header ──────────────────────────────────────────────────────────────

function OrdersPageHeader({ canCreateOrder }: { canCreateOrder: boolean }) {
  return (
    <div className="flex flex-col gap-3 px-6 pt-6 pb-4 border-b border-(--color-border) shrink-0">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-[3.5rem] font-extrabold tracking-tight leading-[1.02] text-(--color-text-primary)">
              <Trans>Orders</Trans>
            </h1>
            <span className="mb-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-(--color-order-completed-bg) text-(--color-order-completed-fg) text-[0.75rem] font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-(--color-success) animate-pulse" />
              <Trans>Live · auto-refresh</Trans>
            </span>
          </div>
          <p className="text-[0.9375rem] text-(--color-text-secondary) mt-1">
            <Trans>Every ticket from fire to handoff. Move them through the line at a glance.</Trans>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 pt-1">
          <LocationSelector triggerClassName="h-10 px-3 rounded-lg bg-(--color-surface-content) border border-(--color-border) text-[0.8125rem] font-semibold text-(--color-text-primary) transition-colors hover:bg-(--color-surface-accent)" />
          <Button variant="outline" className="h-10 px-4 gap-2 text-[0.875rem] font-semibold">
            <Download className="w-4 h-4" />
            <Trans>Export</Trans>
          </Button>
          {canCreateOrder && (
            <Button asChild className="h-10 px-4 gap-2 text-[0.875rem] font-semibold">
              <Link to="/new-order">
                <Plus className="w-4 h-4" />
                <Trans>New Order</Trans>
              </Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Tablet header ──────────────────────────────────────────────────────────
// On tablet the page owns the app's top bar (rendered into the PageHeader slot),
// so the header is a compact single row: title + Live, location, search, new.

function OrdersTabletHeader({
  query,
  setQuery,
  canCreateOrder,
}: {
  query: string;
  setQuery: (value: string) => void;
  canCreateOrder: boolean;
}) {
  const { t } = useLingui();
  return (
    <div className="flex h-15.5 items-center gap-3 border-b border-(--color-border) px-5">
      <div className="flex items-center gap-2 shrink-0">
        <h1 className="text-[1.375rem] font-extrabold tracking-tight leading-none text-(--color-text-primary)">
          <Trans>Orders</Trans>
        </h1>
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-(--color-order-completed-bg) text-(--color-order-completed-fg) text-[0.6875rem] font-bold">
          <span className="w-1.5 h-1.5 rounded-full bg-(--color-success) animate-pulse" />
          <Trans>Live</Trans>
        </span>
      </div>
      <LocationSelector triggerClassName="h-9 px-3 rounded-lg bg-(--color-surface-content) border border-(--color-border) text-[0.8125rem] font-semibold text-(--color-text-primary) shrink-0 transition-colors hover:bg-(--color-surface-accent)" />
      <div className="flex-1" />
      <div className="relative hidden sm:block w-70 max-w-[40vw]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-(--color-text-secondary)" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t`Search ticket…`}
          className="h-10 rounded-lg border-(--color-border) bg-(--color-surface-muted) pl-9 text-sm"
        />
      </div>
      <button
        type="button"
        title={t`Notifications`}
        className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-(--color-border) text-(--color-text-primary) transition-colors hover:bg-(--color-surface-accent)"
      >
        <span className="sr-only">{t`Notifications`}</span>
        <Bell className="w-4.5 h-4.5" />
        <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-(--color-danger)" />
      </button>
      {canCreateOrder && (
        <Button asChild className="h-10 px-4 gap-2 text-[0.875rem] font-semibold shrink-0">
          <Link to="/new-order">
            <Plus className="w-4 h-4" />
            <Trans>New</Trans>
          </Link>
        </Button>
      )}
    </div>
  );
}

// ─── Shared grid ──────────────────────────────────────────────────────────────

const GRID = 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4';

// ─── Main page ────────────────────────────────────────────────────────────────

function OrdersPage() {
  const { t } = useLingui();
  const { hasPermission, isLoading: authLoading } = useAuth();
  const { activeLocation } = useActiveLocation();
  const locationId = activeLocation?.id;

  const canRead = hasPermission('order:read');
  const canUpdate = hasPermission('order:update');
  const canCreateOrder = hasPermission('order:create');

  const { data: orders = [], isLoading } = useOrders(
    { locationId },
    { enabled: canRead && !authLoading },
  );

  const queryClient = useQueryClient();
  const bulkAction = useBulkActionCookingSessions();
  const passOrderMut = usePassOrder();
  const completeOrderMut = useCompleteOrder();

  // On a tablet viewport (768–1279px) the page defaults to the kanban board;
  // desktop defaults to grid. A manual toggle sets an override that sticks.
  const isTablet = useIsTablet();
  type ViewMode = 'grid' | 'rows' | 'board';
  const [viewOverride, setViewOverride] = useState<ViewMode | null>(null);
  const viewMode: ViewMode = viewOverride ?? (isTablet ? 'board' : 'grid');
  const setViewMode = setViewOverride;
  const [serviceFilter, setServiceFilter] = useState<ServiceType | 'all'>('all');
  const [query, setQuery] = useState('');
  /** Orders whose cooking "Complete" API call is in flight */
  const [pendingCookingIds, setPendingCookingIds] = useState<Set<number>>(new Set());
  /** Orders whose plating "Mark Ready" API call is in flight */
  const [pendingPlatingIds, setPendingPlatingIds] = useState<Set<number>>(new Set());
  /** Orders whose hand-off / delivered API call is in flight (optimistic hide) */
  const [pendingFulfilledIds, setPendingFulfilledIds] = useState<Set<number>>(new Set());
  const [toastMsg, setToastMsg] = useState('');
  const [toastVisible, setToastVisible] = useState(false);

  const sectionRefs = useRef<Partial<Record<KpiKey, HTMLElement | null>>>({});

  // Bucket orders by derived stage
  const buckets = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = orders.filter((o) => {
      if (pendingFulfilledIds.has(o.id)) return false;
      if (serviceFilter !== 'all' && o.serviceType !== serviceFilter) return false;
      if (
        q &&
        !o.tkt.toLowerCase().includes(q) &&
        !o.title.toLowerCase().includes(q) &&
        !(o.guestName?.toLowerCase().includes(q) ?? false) &&
        !o.allItems.some((it) => it.toLowerCase().includes(q))
      ) {
        return false;
      }
      return true;
    });

    return {
      cooking:   filtered.filter((o) => deriveStage(o) === 'cooking'),
      plating:   filtered.filter((o) => deriveStage(o) === 'plating'),
      pickup:    filtered.filter((o) => deriveStage(o) === 'pickup'),
      delivery:  filtered.filter((o) => deriveStage(o) === 'delivery'),
      completed: filtered.filter((o) => deriveStage(o) === 'completed'),
      // "Just out the door" rail — any order that has left the kitchen
      // (plating, pickup, delivery) regardless of timestamp.
      recent: filtered.filter((o) => {
        const stage = deriveStage(o);
        return stage === 'plating' || stage === 'pickup' || stage === 'delivery';
      }).sort((a, b) => {
        const ta = new Date(a.passedAt ?? a.sentAt ?? a.createdAt).getTime();
        const tb = new Date(b.passedAt ?? b.sentAt ?? b.createdAt).getTime();
        return tb - ta;
      }),
    };
  }, [orders, pendingFulfilledIds, serviceFilter, query]);

  const showToast = useCallback((msg: string) => {
    setToastMsg(msg);
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 2500);
  }, []);

  /**
   * Cooking "Complete" — bulk-completes all active sessions for the order,
   * then optimistically moves it to Plating.
   */
  const dismissCooking = useCallback(async (id: number) => {
    if (!canUpdate) { showToast(t`Insufficient permissions. Required: order:update.`); return; }
    const order = orders.find((o) => o.id === id);
    if (!order) return;

    const activeIds = order.sessions
      .filter((s) => s.status !== 'COMPLETED' && s.status !== 'CANCELLED')
      .map((s) => s.id);

    setPendingCookingIds((prev) => new Set([...prev, id]));
    try {
      if (activeIds.length > 0) {
        await bulkAction.mutateAsync({ ids: activeIds, action: 'COMPLETED' });
      }
      // Optimistic: sessions done → isActive=false, isPlating=true
      queryClient.setQueriesData<BoardOrder[]>(
        { queryKey: orderKeys.lists() },
        (old) => old?.map((o) =>
          o.id === id
            ? {
                ...o,
                isActive: false,
                isPaused: false,
                isPlating: true,
                sessions: o.sessions.map((s) =>
                  s.status !== 'COMPLETED' && s.status !== 'CANCELLED'
                    ? { ...s, status: 'COMPLETED' as BoardSessionStatus }
                    : s,
                ),
              }
            : o,
        ),
      );
      showToast(t`Ticket sent to plating`);
    } catch {
      toast.error(t`Could not complete — please try again`);
    } finally {
      setPendingCookingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [canUpdate, orders, bulkAction, queryClient, showToast, t]);

  /**
   * Plating "Mark Ready" — passes the order through the expo window.
   * Optimistically sets isPlating=false so it moves to pickup/delivery/completed.
   */
  const passPlating = useCallback(async (id: number) => {
    if (!canUpdate) { showToast(t`Insufficient permissions. Required: order:update.`); return; }
    setPendingPlatingIds((prev) => new Set([...prev, id]));
    try {
      await passOrderMut.mutateAsync(id);
      // Optimistic: mark as passed
      queryClient.setQueriesData<BoardOrder[]>(
        { queryKey: orderKeys.lists() },
        (old) => old?.map((o) =>
          o.id === id ? { ...o, isPlating: false, passedAt: new Date().toISOString() } : o,
        ),
      );
      showToast(t`Order marked ready`);
    } catch {
      toast.error(t`Could not mark ready — please try again`);
    } finally {
      setPendingPlatingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [canUpdate, passOrderMut, queryClient, showToast, t]);

  /**
   * Pickup "Hand off" / Delivery "Delivered" — sets order status to COMPLETED
   * via the API so it stays off the board after reload.
   */
  const dismissFulfilled = useCallback(async (id: number) => {
    if (!canUpdate) { showToast(t`Insufficient permissions. Required: order:update.`); return; }
    setPendingFulfilledIds((prev) => new Set([...prev, id]));
    try {
      await completeOrderMut.mutateAsync(id);
      // Optimistic: mark as COMPLETED so deriveStage routes the order to 'completed'.
      queryClient.setQueriesData<BoardOrder[]>(
        { queryKey: orderKeys.lists() },
        (old) => old?.map((o) =>
          o.id === id ? { ...o, status: 'COMPLETED' as const } : o,
        ),
      );
      setPendingFulfilledIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      showToast(t`Order fulfilled`);
    } catch {
      setPendingFulfilledIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.error(t`Could not fulfill order — please try again`);
    }
  }, [canUpdate, completeOrderMut, queryClient, showToast, t]);

  // useCallback so the stable reference is safe to pass as onClick in JSX
  // without the React Compiler flagging ref access during array construction.
  const handleKpi = useCallback((id: KpiKey) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  type ServiceFilterValue = ServiceType | 'all';
  const SERVICE_TABS: Array<{ value: ServiceFilterValue; label: string }> = [
    { value: 'all',      label: t`All stages` },
    { value: 'DINE_IN',  label: t`Dine-in` },
    { value: 'TAKEOUT',  label: t`Takeout` },
    { value: 'DELIVERY', label: t`Delivery` },
  ];

  // onClick is intentionally omitted here — it's passed directly in the JSX map
  // so the React Compiler doesn't see a ref-capturing closure inside an array
  // that is itself constructed during render.
  type KpiConfig = Omit<KpiCardProps, 'onClick'>;
  const KPI_CONFIG: KpiConfig[] = [
    {
      id: 'cooking',   label: t`Cooking`,          count: buckets.cooking.length,
      icon: Flame,      iconBg: 'bg-(--color-order-cooking-bg)',   iconColor: 'text-(--color-order-cooking-fg)',
    },
    {
      id: 'plating',   label: t`Plating & QC`,     count: buckets.plating.length,
      icon: Utensils,   iconBg: 'bg-(--color-order-plating-bg)',   iconColor: 'text-(--color-order-plating-fg)',
    },
    {
      id: 'pickup',    label: t`Ready · pickup`,   count: buckets.pickup.length,
      icon: BellRing,   iconBg: 'bg-(--color-order-pickup-bg)',    iconColor: 'text-(--color-order-pickup-fg)',
    },
    {
      id: 'delivery',  label: t`Out for delivery`, count: buckets.delivery.length,
      icon: Navigation, iconBg: 'bg-(--color-order-cooking-bg)',   iconColor: 'text-(--color-order-cooking-fg)',
    },
    {
      id: 'completed', label: t`Completed today`,  count: buckets.completed.length,
      icon: CheckCircle2, iconBg: 'bg-(--color-order-completed-bg)', iconColor: 'text-(--color-order-completed-fg)',
    },
  ];

  if (!authLoading && !canRead) {
    return (
      <Navigate
        to="/unauthorized"
        search={{ reason: 'permission_denied', from: '/orders', requiredPermission: 'order:read' }}
        replace
      />
    );
  }

  // Shared toolbar (service filter · sort/filter · view toggle) — rendered in
  // every view. Kept as a const so the board and stacked layouts share one copy.
  const toolbar = (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-1 bg-(--color-surface-muted) p-1 rounded-xl border border-(--color-border)">
        {SERVICE_TABS.map(tab => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setServiceFilter(tab.value)}
            className={cn(
              'px-3.5 py-1.5 rounded-lg font-semibold text-[0.8125rem] transition-colors',
              serviceFilter === tab.value
                ? 'bg-(--color-text-primary) text-(--color-surface-page) shadow-sm'
                : 'text-(--color-text-secondary) hover:text-(--color-text-primary)',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="flex-1" />
      <Button variant="outline" className="h-10 px-3.5 gap-2 text-[0.8125rem] font-semibold">
        <ArrowUpDown className="w-4 h-4" />
        <Trans>Sort</Trans>
      </Button>
      <Button variant="outline" className="h-10 px-3.5 gap-2 text-[0.8125rem] font-semibold">
        <SlidersHorizontal className="w-4 h-4" />
        <Trans>Filter</Trans>
      </Button>
      <div className="flex items-center bg-(--color-surface-content) border border-(--color-border) rounded-lg h-10 p-1 gap-0.5">
        <Button
          variant="ghost"
          size="icon"
          aria-label={t`List view`}
          onClick={() => setViewMode('rows')}
          className={cn('size-8', viewMode === 'rows' ? 'bg-(--color-surface-muted) text-(--color-text-primary)' : 'text-(--color-text-secondary) hover:text-(--color-text-primary)')}
        >
          <List className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t`Grid view`}
          onClick={() => setViewMode('grid')}
          className={cn('size-8', viewMode === 'grid' ? 'bg-(--color-surface-muted) text-(--color-text-primary)' : 'text-(--color-text-secondary) hover:text-(--color-text-primary)')}
        >
          <LayoutGrid className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t`Board view`}
          onClick={() => setViewMode('board')}
          className={cn('size-8', viewMode === 'board' ? 'bg-(--color-surface-muted) text-(--color-text-primary)' : 'text-(--color-text-secondary) hover:text-(--color-text-primary)')}
        >
          <Columns3 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  // On tablet the Orders header becomes the app's main top bar (rendered into
  // the PageHeader slot); on desktop it's the full inline page header.
  const header = isTablet ? (
    <PageHeader>
      <OrdersTabletHeader query={query} setQuery={setQuery} canCreateOrder={canCreateOrder} />
    </PageHeader>
  ) : (
    <OrdersPageHeader canCreateOrder={canCreateOrder} />
  );

  // Board (tablet kanban) is a full-height, internally-scrolling layout, so the
  // page itself must not scroll; the stacked grid/rows views scroll normally.
  if (viewMode === 'board') {
    return (
      <div className="flex flex-col h-full min-h-0 overflow-hidden">
        {header}
        <div className="flex-1 min-h-0 flex flex-col gap-4 px-6 pt-5 pb-5">
          <RecentRail orders={buckets.recent} className="shrink-0" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 shrink-0">
            {KPI_CONFIG.map(cfg => <KpiCard key={cfg.id} {...cfg} onClick={handleKpi} />)}
          </div>
          {toolbar}
          <div className="flex-1 min-h-0">
            <OrdersKanban
              buckets={buckets}
              onCookingComplete={dismissCooking}
              onPlatingPass={passPlating}
              onFulfilled={dismissFulfilled}
              pendingCookingIds={pendingCookingIds}
              pendingPlatingIds={pendingPlatingIds}
              pendingFulfilledIds={pendingFulfilledIds}
              canUpdate={canUpdate}
            />
          </div>
        </div>
        <Toast message={toastMsg} visible={toastVisible} />
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-y-auto">

      {header}

      {/* ── Board content ──────────────────────────────────────────── */}
      <div className="px-6 pt-7 pb-10 flex flex-col">

        {/* ── Recently completed rail ─────────────────────────────── */}
        <RecentRail orders={buckets.recent} className="mb-8" />

        {/* ── KPI cards ──────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {KPI_CONFIG.map(cfg => <KpiCard key={cfg.id} {...cfg} onClick={handleKpi} />)}
        </div>

        {/* ── Toolbar ────────────────────────────────────────────── */}
        <div className="mb-8">{toolbar}</div>

        {/* ── Cooking now ────────────────────────────────────────── */}
        <section
          id="cooking"
          data-testid="cooking-section"
          ref={el => { sectionRefs.current.cooking = el; }}
          className="mb-10 scroll-mt-4"
        >
          <SectionHeader
            title={t`Cooking now`}
            badgeLabel={t`${buckets.cooking.length} ACTIVE`}
            badgeClass="bg-(--color-order-cooking-bg) text-(--color-order-cooking-fg)"
          />
          {isLoading ? (
            viewMode === 'grid'
              ? <div className={GRID}>{[0, 1, 2].map(i => <CardSkeleton key={i} />)}</div>
              : <div className="flex flex-col gap-1.5">{[0, 1, 2].map(i => <RowSkeleton key={i} />)}</div>
          ) : viewMode === 'grid' ? (
            <div className={GRID}>
              {buckets.cooking.map(o => (
                <CookingCard key={o.id} order={o} onDismiss={dismissCooking} completePending={pendingCookingIds.has(o.id)} canComplete={canUpdate} />
              ))}
            </div>
          ) : (
            buckets.cooking.length > 0 && (
              <Table>
                <TableHeader className="bg-(--color-surface-muted)">
                  <CookingRowHeader />
                </TableHeader>
                <TableBody>
                  {buckets.cooking.map(o => (
                    <CookingRow key={o.id} order={o} onDismiss={dismissCooking} completePending={pendingCookingIds.has(o.id)} />
                  ))}
                </TableBody>
              </Table>
            )
          )}
          {!isLoading && buckets.cooking.length === 0 && (
            <div className="py-12 flex flex-col items-center gap-2 text-(--color-text-secondary) border border-dashed border-(--color-border) rounded-xl">
              <Flame className="h-8 w-8 opacity-30" />
              <p className="text-[0.875rem]"><Trans>No active tickets on the fire.</Trans></p>
            </div>
          )}
        </section>

        {/* ── Plating & QC ───────────────────────────────────────── */}
        <section
          id="plating"
          data-testid="plating-section"
          ref={el => { sectionRefs.current.plating = el; }}
          className="mb-10 scroll-mt-4"
        >
          <SectionHeader
            title={t`Plating & QC`}
            badgeLabel={t`${buckets.plating.length} ON PASS`}
            badgeClass="bg-(--color-order-plating-bg) text-(--color-order-plating-fg)"
          />
          {isLoading ? (
            viewMode === 'grid'
              ? <div className={GRID}>{[0, 1].map(i => <CardSkeleton key={i} />)}</div>
              : <div className="flex flex-col gap-1.5">{[0, 1].map(i => <RowSkeleton key={i} />)}</div>
          ) : viewMode === 'grid' ? (
            <div className={GRID}>
              {buckets.plating.map(o => (
                <PlatingCard key={o.id} order={o} onPass={passPlating} canPass={canUpdate} />
              ))}
            </div>
          ) : (
            buckets.plating.length > 0 && (
              <Table>
                <TableHeader className="bg-(--color-order-plating-bg)">
                  <PlatingRowHeader />
                </TableHeader>
                <TableBody>
                  {buckets.plating.map(o => (
                    <PlatingRow key={o.id} order={o} onPass={passPlating} passPending={pendingPlatingIds.has(o.id)} />
                  ))}
                </TableBody>
              </Table>
            )
          )}
          {!isLoading && buckets.plating.length === 0 && (
            <div className="py-10 flex flex-col items-center gap-2 text-(--color-text-secondary) border border-dashed border-(--color-order-plating-bg) rounded-xl">
              <Utensils className="h-8 w-8 opacity-30" />
              <p className="text-[0.875rem]"><Trans>The pass is clear.</Trans></p>
            </div>
          )}
        </section>

        {/* ── Ready for pickup ───────────────────────────────────── */}
        <section
          id="pickup"
          data-testid="pickup-section"
          ref={el => { sectionRefs.current.pickup = el; }}
          className="mb-10 scroll-mt-4"
        >
          <SectionHeader
            title={t`Ready for pickup`}
            badgeLabel={t`${buckets.pickup.length} ON SHELF`}
            badgeClass="bg-(--color-order-pickup-bg) text-(--color-order-pickup-fg)"
          />
          {isLoading ? (
            viewMode === 'grid'
              ? <div className={GRID}>{[0, 1].map(i => <CardSkeleton key={i} />)}</div>
              : <div className="flex flex-col gap-1.5">{[0, 1].map(i => <RowSkeleton key={i} />)}</div>
          ) : viewMode === 'grid' ? (
            <div className={GRID}>
              {buckets.pickup.map(o => <PickupCard key={o.id} order={o} onDismiss={dismissFulfilled} />)}
            </div>
          ) : (
            buckets.pickup.length > 0 && (
              <Table>
                <TableHeader className="bg-(--color-surface-muted)">
                  <PickupRowHeader />
                </TableHeader>
                <TableBody>
                  {buckets.pickup.map(o => <PickupRow key={o.id} order={o} onDismiss={dismissFulfilled} />)}
                </TableBody>
              </Table>
            )
          )}
          {!isLoading && buckets.pickup.length === 0 && (
            <div className="py-10 flex flex-col items-center gap-2 text-(--color-text-secondary) border border-dashed border-(--color-border) rounded-xl">
              <BellRing className="h-8 w-8 opacity-30" />
              <p className="text-[0.875rem]"><Trans>Counter is clear.</Trans></p>
            </div>
          )}
        </section>

        {/* ── Out for delivery ───────────────────────────────────── */}
        <section
          id="delivery"
          data-testid="delivery-section"
          ref={el => { sectionRefs.current.delivery = el; }}
          className="mb-10 scroll-mt-4"
        >
          <SectionHeader
            title={t`Out for delivery`}
            badgeLabel={t`${buckets.delivery.length} EN ROUTE`}
            badgeClass="bg-(--color-order-cooking-bg) text-(--color-order-cooking-fg)"
          />
          {isLoading ? (
            viewMode === 'grid'
              ? <div className={GRID}>{[0, 1].map(i => <CardSkeleton key={i} />)}</div>
              : <div className="flex flex-col gap-1.5">{[0, 1].map(i => <RowSkeleton key={i} />)}</div>
          ) : viewMode === 'grid' ? (
            <div className={GRID}>
              {buckets.delivery.map(o => <DeliveryCard key={o.id} order={o} onDismiss={dismissFulfilled} />)}
            </div>
          ) : (
            buckets.delivery.length > 0 && (
              <Table>
                <TableHeader className="bg-(--color-surface-muted)">
                  <DeliveryRowHeader />
                </TableHeader>
                <TableBody>
                  {buckets.delivery.map(o => <DeliveryRow key={o.id} order={o} onDismiss={dismissFulfilled} />)}
                </TableBody>
              </Table>
            )
          )}
          {!isLoading && buckets.delivery.length === 0 && (
            <div className="py-10 flex flex-col items-center gap-2 text-(--color-text-secondary) border border-dashed border-(--color-border) rounded-xl">
              <Navigation className="h-8 w-8 opacity-30" />
              <p className="text-[0.875rem]"><Trans>No active deliveries.</Trans></p>
            </div>
          )}
        </section>

        {/* ── Completed today ────────────────────────────────────── */}
        <section
          id="completed"
          data-testid="completed-section"
          ref={el => { sectionRefs.current.completed = el; }}
          className="mb-6 scroll-mt-4"
        >
          <SectionHeader
            title={t`Completed today`}
            badgeLabel={t`${buckets.completed.length} CLOSED`}
            badgeClass="bg-(--color-order-completed-bg) text-(--color-order-completed-fg)"
          />
          {buckets.completed.length > 0 ? (
            <Table>
              <TableHeader className="bg-(--color-surface-muted)">
                <CompletedRowHeader />
              </TableHeader>
              <TableBody>
                {buckets.completed.map(o => <CompletedRow key={o.id} order={o} />)}
              </TableBody>
            </Table>
          ) : (
            <div className="py-10 text-center text-[0.875rem] text-(--color-text-secondary)">
              <Trans>No completed orders today.</Trans>
            </div>
          )}
        </section>

      </div>

      <Toast message={toastMsg} visible={toastVisible} />
    </div>
  );
}
